import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

import { AppContextProvider } from './store/app-context';
import { AuthContextProvider } from './store/auth-context';
import { ReaderContextProvider } from './store/reader-context';

import './index.scss';
import App from './components/App';
import UpdatePrompt from './components/UpdatePrompt';

import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      gcTime: 24 * 60 * 60 * 1000, // 24h — persisted entries must not GC while offline
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'logistics_rq_cache',
  throttleTime: 1000,
});

// Bump on schema-breaking changes to drop stale persisted caches
const CACHE_BUSTER = 'v1';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <AppContextProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          buster: CACHE_BUSTER,
          maxAge: 24 * 60 * 60 * 1000,
          dehydrateOptions: {
            shouldDehydrateQuery: (query) => {
              if (query.state.status !== 'success') return false;
              const key = query.queryKey[0];
              return key === 'courier-routes' || key === 'box-statuses';
            },
          },
        }}
      >
        <AuthContextProvider>
          <ReaderContextProvider>
            <App />
            <UpdatePrompt />
          </ReaderContextProvider>
        </AuthContextProvider>
      </PersistQueryClientProvider>
    </AppContextProvider>
  </React.StrictMode>
);

// Registered in production only (gated inside register()). Caches the app shell so
// couriers can reload the PWA while offline. Does NOT cache API responses —
// service-worker.ts precaches build assets and serves index.html for navigations only.
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    if (registration.waiting) {
      // eslint-disable-next-line no-console
      console.log('[SW] New version available — reload to activate.');
      // Surface the waiting worker to the UI banner (UpdatePrompt) so couriers
      // running long-lived PWA tabs see the new build without having to close.
      window.dispatchEvent(
        new CustomEvent('sw-update-available', { detail: registration.waiting }),
      );
    }
  },
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
